// HerbalSoapContact.js - Herbal soap themed contact section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Contact section with a form and contact information
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the contact section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createHerbalSoapContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const contactSectionClass = 'herbal-soap-contact-section';
  const contactContainerClass = 'herbal-soap-contact-container';
  const contactHeaderClass = 'herbal-soap-contact-header';
  const contactTitleClass = 'herbal-soap-contact-title';
  const contactSubtitleClass = 'herbal-soap-contact-subtitle';
  const contactContentClass = 'herbal-soap-contact-content';
  const contactInfoClass = 'herbal-soap-contact-info';
  const contactInfoItemClass = 'herbal-soap-contact-info-item';
  const contactInfoIconClass = 'herbal-soap-contact-info-icon';
  const contactInfoTextClass = 'herbal-soap-contact-info-text';
  const contactFormWrapperClass = 'herbal-soap-contact-form-wrapper';
  const contactFormClass = 'herbal-soap-contact-form';
  const contactFormGroupClass = 'herbal-soap-contact-form-group';
  const contactFormLabelClass = 'herbal-soap-contact-form-label';
  const contactFormInputClass = 'herbal-soap-contact-form-input';
  const contactFormTextareaClass = 'herbal-soap-contact-form-textarea';
  const contactFormButtonClass = 'herbal-soap-contact-form-button';
  const contactFormSuccessClass = 'herbal-soap-contact-form-success';
  const contactFormErrorClass = 'herbal-soap-contact-form-error';
  
  // Desktop Styles - Herbal theme with natural green colors
  const contactSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f0fdf4',
    background: `
      radial-gradient(circle at 20% 80%, rgba(144, 238, 144, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 139, 34, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const contactContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const contactHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const contactTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const contactSubtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const contactContentStyles = {
    display: 'flex',
    width: '100%',
    gap: '50px',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  };

  const contactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    flex: '1',
    maxWidth: '400px'
  };

  const contactInfoItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '25px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid rgba(144, 238, 144, 0.25)',
    boxShadow: '0 4px 15px rgba(34, 139, 34, 0.08)',
    transition: 'all 0.3s ease'
  };

  const contactInfoIconStyles = {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    backgroundColor: '#90EE90',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: '20px',
    padding: '8px',
    flexShrink: '0',
    boxShadow: '0 4px 12px rgba(144, 238, 144, 0.3)'
  };

  const contactInfoTextStyles = {
    fontSize: '16px',
    color: '#4a5568',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormWrapperStyles = {
    flex: '1',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid rgba(144, 238, 144, 0.25)',
    boxShadow: '0 10px 30px rgba(34, 139, 34, 0.12)',
    padding: '40px',
    position: 'relative',
    overflow: 'hidden'
  };

  const contactFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    width: '100%',
    position: 'relative',
    zIndex: '2'
  };

  const contactFormGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const contactFormLabelStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a3d1a',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormInputStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #dcfce7',
    fontSize: '16px',
    color: '#1a3d1a',
    backgroundColor: '#f0fdf4',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormInputFocusStyles = {
    borderColor: '#90EE90',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 0 3px rgba(144, 238, 144, 0.15)'
  };

  const contactFormTextareaStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #dcfce7',
    fontSize: '16px',
    color: '#1a3d1a',
    backgroundColor: '#f0fdf4',
    fontFamily: '"Inter", sans-serif',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormButtonStyles = {
    padding: '16px 32px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#228b22',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(34, 139, 34, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  };

  const contactFormButtonHoverStyles = {
    background: 'linear-gradient(135deg, #1a3d1a 0%, #228b22 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(34, 139, 34, 0.4)'
  };

  const contactFormSuccessStyles = {
    padding: '16px 20px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #90EE90',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormErrorStyles = {
    padding: '16px 20px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #ef4444',
    fontFamily: '"Inter", sans-serif'
  };
  
  // Tablet Styles
  const tabletContactContentStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px'
  };
  
  const tabletContactInfoStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  const tabletContactFormWrapperStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  // Mobile Styles
  const mobileContactSectionStyles = {
    ...contactSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const mobileContactHeaderStyles = {
    marginBottom: '30px'
  };
  
  const mobileContactTitleStyles = {
    fontSize: '32px'
  };
  
  const mobileContactSubtitleStyles = {
    fontSize: '18px'
  };
  
  const mobileContactFormWrapperStyles = {
    padding: '30px 20px'
  };

  const mobileContactInfoItemStyles = {
    padding: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(contactSectionClass, contactSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContainerClass, contactContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactHeaderClass, contactHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(contactTitleClass, contactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSubtitleClass, contactSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContentClass, contactContentStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoClass, contactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoItemClass, contactInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoIconClass, contactInfoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoTextClass, contactInfoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormWrapperClass, contactFormWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormClass, contactFormStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormGroupClass, contactFormGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormLabelClass, contactFormLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormInputClass, contactFormInputStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormTextareaClass, contactFormTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormButtonClass, contactFormButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormSuccessClass, contactFormSuccessStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormErrorClass, contactFormErrorStyles);
    
    // Hover and focus styles
    newRegistry[DEVICES.DESKTOP].set(`${contactFormInputClass}:focus`, contactFormInputFocusStyles);
    newRegistry[DEVICES.DESKTOP].set(`${contactFormTextareaClass}:focus`, contactFormInputFocusStyles);
    newRegistry[DEVICES.DESKTOP].set(`${contactFormButtonClass}:hover`, contactFormButtonHoverStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contactContentClass, tabletContactContentStyles);
    newRegistry[DEVICES.TABLET].set(contactInfoClass, tabletContactInfoStyles);
    newRegistry[DEVICES.TABLET].set(contactFormWrapperClass, tabletContactFormWrapperStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(contactSectionClass, mobileContactSectionStyles);
    newRegistry[DEVICES.MOBILE].set(contactHeaderClass, mobileContactHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(contactTitleClass, mobileContactTitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactSubtitleClass, mobileContactSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactFormWrapperClass, mobileContactFormWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(contactInfoItemClass, mobileContactInfoItemStyles);
    
    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    contactSectionClass,
    contactContainerClass,
    contactHeaderClass,
    contactTitleClass,
    contactSubtitleClass,
    contactContentClass,
    contactInfoClass,
    contactInfoItemClass,
    contactInfoIconClass,
    contactInfoTextClass,
    contactFormWrapperClass,
    contactFormClass,
    contactFormGroupClass,
    contactFormLabelClass,
    contactFormInputClass,
    contactFormTextareaClass,
    contactFormButtonClass,
    contactFormSuccessClass,
    contactFormErrorClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  
  // Header elements
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  
  // Content container
  const contentId = sectionId + 5;
  
  // Contact information elements
  const infoContainerId = sectionId + 6;
  
  // Address info
  const addressItemId = sectionId + 7;
  const addressIconId = sectionId + 8;
  const addressTextId = sectionId + 9;
  const addressTextLine2Id = sectionId + 100;
  
  // Phone info
  const phoneItemId = sectionId + 10;
  const phoneIconId = sectionId + 11;
  const phoneTextId = sectionId + 12;
  const phoneTextLine2Id = sectionId + 101;
  
  // Email info
  const emailItemId = sectionId + 13;
  const emailIconId = sectionId + 14;
  const emailTextId = sectionId + 15;
  const emailTextLine2Id = sectionId + 102;
  
  // Form elements
  const formWrapperId = sectionId + 16;
  const formId = sectionId + 17;
  
  // Name field
  const nameGroupId = sectionId + 18;
  const nameLabelId = sectionId + 19;
  const nameInputId = sectionId + 20;
  
  // Email field
  const emailGroupId = sectionId + 21;
  const emailLabelId = sectionId + 22;
  const emailInputId = sectionId + 23;
  
  // Message field
  const messageGroupId = sectionId + 24;
  const messageLabelId = sectionId + 25;
  const messageTextareaId = sectionId + 26;
  
  // Submit button and status messages
  const submitButtonId = sectionId + 27;
  const successMessageId = sectionId + 28;
  const errorMessageId = sectionId + 29;
  
  const newElements = [
    // Main Contact Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Contact Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Contact Section',
      className: contactSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-section',
        'data-component': 'section',
        'data-section-type': 'contact',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: 'Contact Container',
      className: contactContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: 'Contact Header',
      className: contactHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: 'Contact Title',
      className: contactTitleClass,
      styles: {},
      children: [],
      text: 'Get In Touch',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: 'Contact Subtitle',
      className: contactSubtitleClass,
      styles: {},
      children: [],
      text: 'Ready to experience the pure goodness of nature? Contact us for premium herbal soaps, custom orders, wholesale inquiries, or any questions about our natural skincare products.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: 'Contact Content',
      className: contactContentClass,
      styles: {},
      children: [infoContainerId, formWrapperId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-content'
      }
    },
    
    // Contact Info Container
    {
      id: infoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info',
      parentId: contentId,
      hierarchyName: 'Contact Info',
      className: contactInfoClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info'
      }
    },
    
    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Item',
      parentId: infoContainerId,
      hierarchyName: 'Address Item',
      className: contactInfoItemClass,
      styles: {},
      children: [addressIconId, addressTextId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-item'
      }
    },
    
    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: 'Address Icon',
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '🏠',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-icon'
      }
    },
    
    // Address Text Container
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text Container',
      parentId: addressItemId,
      hierarchyName: 'Address Text Container',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [addressTextLine2Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text-container'
      }
    },
    
    // Address Text Line 1
    {
      id: addressTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text',
      parentId: addressTextId,
      hierarchyName: 'Address Text',
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#1a3d1a',
        marginBottom: '4px'
      },
      children: [],
      text: '123 Herbal Gardens Lane, Natural Valley, Green City 12345',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text'
      }
    },
    
    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Item',
      parentId: infoContainerId,
      hierarchyName: 'Phone Item',
      className: contactInfoItemClass,
      styles: {},
      children: [phoneIconId, phoneTextId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-item'
      }
    },
    
    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: 'Phone Icon',
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '📞',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-icon'
      }
    },
    
    // Phone Text Container
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text Container',
      parentId: phoneItemId,
      hierarchyName: 'Phone Text Container',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [phoneTextLine2Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text-container'
      }
    },
    
    // Phone Text
    {
      id: phoneTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text',
      parentId: phoneTextId,
      hierarchyName: 'Phone Text',
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#1a3d1a',
        marginBottom: '4px'
      },
      children: [],
      text: 'Call Us: 0308-6922228',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text'
      }
    },
    
    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Item',
      parentId: infoContainerId,
      hierarchyName: 'Email Item',
      className: contactInfoItemClass,
      styles: {},
      children: [emailIconId, emailTextId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-item'
      }
    },
    
    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: 'Email Icon',
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '✉️',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-icon'
      }
    },
    
    // Email Text Container
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text Container',
      parentId: emailItemId,
      hierarchyName: 'Email Text Container',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [emailTextLine2Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text-container'
      }
    },
    
    // Email Text
    {
      id: emailTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text',
      parentId: emailTextId,
      hierarchyName: 'Email Text',
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#1a3d1a',
        marginBottom: '4px'
      },
      children: [],
      text: 'Email: rosemeenrao5@gmail.com',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-info-text'
      }
    },
    
    // Form Wrapper
    {
      id: formWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Wrapper',
      parentId: contentId,
      hierarchyName: 'Form Wrapper',
      className: contactFormWrapperClass,
      styles: {},
      children: [formId, successMessageId, errorMessageId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-form-wrapper'
      }
    },
    
    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formWrapperId,
      hierarchyName: 'Contact Form',
      className: contactFormClass,
      styles: {},
      children: [nameGroupId, emailGroupId, messageGroupId, submitButtonId],
      method: 'post',
      action: '',
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-contact-form',
        'data-form-type': 'contact'
      }
    },
    
    // Name Field Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Field Group',
      parentId: formId,
      hierarchyName: 'Name Field Group',
      className: contactFormGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-group'
      }
    },
    
    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: 'Name Label',
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Full Name *',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-label',
        'for': 'herbal-soap-contact-name'
      }
    },
    
    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: 'Name Input',
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-input',
        'type': 'text',
        'id': 'herbal-soap-contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Field Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Field Group',
      parentId: formId,
      hierarchyName: 'Email Field Group',
      className: contactFormGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: 'Email Label',
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Email Address *',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-label',
        'for': 'herbal-soap-contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: 'Email Input',
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-input',
        'type': 'email',
        'id': 'herbal-soap-contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Message Field Group
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Field Group',
      parentId: formId,
      hierarchyName: 'Message Field Group',
      className: contactFormGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-group'
      }
    },

    // Message Label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: 'Message Label',
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Message *',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-label',
        'for': 'herbal-soap-contact-message'
      }
    },

    // Message Textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: 'Message Textarea',
      className: contactFormTextareaClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-textarea',
        'id': 'herbal-soap-contact-message',
        'name': 'message',
        'placeholder': 'Tell us about your skincare needs, product inquiries, custom orders, wholesale requests, or any questions about our natural herbal soaps...',
        'required': 'true',
        'rows': '5'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: 'Submit Button',
      className: contactFormButtonClass,
      styles: {},
      children: [],
      text: 'Send Message',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-button',
        'type': 'submit'
      }
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formWrapperId,
      hierarchyName: 'Success Message',
      className: contactFormSuccessClass,
      styles: {},
      children: [],
      text: 'Thank you for reaching out! We\'ll get back to you within 24 hours with information about our natural herbal soaps and how we can help with your skincare needs.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-success-message'
      }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formWrapperId,
      hierarchyName: 'Error Message',
      className: contactFormErrorClass,
      styles: {},
      children: [],
      text: 'There was an error sending your message. Please try again or call us directly at +1 (555) 123-HERB.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-form-error-message'
      }
    }
  ];
  
  return {
    elements: newElements
  };
}; 